import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, FileText, RotateCcw, ArrowRight, Eye, Trash2, Package } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CREDIT_NOTE_REASONS = [
  { value: 'Exchange - Size change', label: 'Exchange – Size change' },
  { value: 'Defective implant', label: 'Defective implant' },
  { value: 'Customer return', label: 'Customer return' },
  { value: 'Pricing correction', label: 'Pricing correction' },
  { value: 'Damaged in transit', label: 'Damaged in transit' },
  { value: 'Wrong product shipped', label: 'Wrong product shipped' },
  { value: 'Quality issue', label: 'Quality issue' },
  { value: 'Other', label: 'Other' }
];

const CreditNotes = () => {
  const navigate = useNavigate();
  const [creditNotes, setCreditNotes] = useState([]);
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isReplacementDialogOpen, setIsReplacementDialogOpen] = useState(false);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    invoice_id: '',
    items: [{ product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }],
    reason: 'Customer return',
    notes: ''
  });
  
  // Replacement form state
  const [replacementItems, setReplacementItems] = useState([
    { product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }
  ]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [creditNotesRes, salesRes, productsRes, inventoryRes] = await Promise.all([
        axios.get(`${API_URL}/credit-notes`),
        axios.get(`${API_URL}/sales`),
        axios.get(`${API_URL}/products`),
        axios.get(`${API_URL}/inventory`)
      ]);
      setCreditNotes(creditNotesRes.data);
      setSales(salesRes.data);
      setProducts(productsRes.data);
      setInventory(inventoryRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceSelect = (invoiceId) => {
    const invoice = sales.find(s => s.id === invoiceId);
    setSelectedInvoice(invoice);
    setFormData({
      ...formData,
      invoice_id: invoiceId,
      items: [{ product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }]
    });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // If product selected, auto-fill from invoice item
    if (field === 'product_id' && selectedInvoice) {
      const invoiceItem = selectedInvoice.items.find(i => i.product_id === value);
      if (invoiceItem) {
        newItems[index].batch_number = invoiceItem.batch_number;
        newItems[index].selling_price = invoiceItem.selling_price;
        newItems[index].gst_percentage = invoiceItem.gst_percentage;
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.invoice_id) {
      toast.error('Please select an invoice');
      return;
    }
    
    const validItems = formData.items.filter(item => 
      item.product_id && item.quantity && item.selling_price
    );
    
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    try {
      const payload = {
        invoice_id: formData.invoice_id,
        items: validItems.map(item => ({
          product_id: item.product_id,
          batch_number: item.batch_number,
          quantity: parseInt(item.quantity),
          selling_price: parseFloat(item.selling_price),
          gst_percentage: parseFloat(item.gst_percentage)
        })),
        reason: formData.reason,
        notes: formData.notes
      };
      
      await axios.post(`${API_URL}/credit-notes`, payload);
      toast.success('Credit note created successfully');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create credit note');
    }
  };

  const handleCreateReplacement = async (e) => {
    e.preventDefault();
    
    const validItems = replacementItems.filter(item => 
      item.product_id && item.batch_number && item.quantity && item.selling_price
    );
    
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/credit-notes/${selectedCreditNote.id}/create-replacement`,
        validItems.map(item => ({
          product_id: item.product_id,
          batch_number: item.batch_number,
          quantity: parseInt(item.quantity),
          selling_price: parseFloat(item.selling_price),
          gst_percentage: parseFloat(item.gst_percentage)
        }))
      );
      toast.success('Replacement invoice created successfully');
      setIsReplacementDialogOpen(false);
      setReplacementItems([{ product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create replacement invoice');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this credit note? This will reverse inventory adjustments.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/credit-notes/${id}`);
      toast.success('Credit note deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete credit note');
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_id: '',
      items: [{ product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }],
      reason: 'Customer return',
      notes: ''
    });
    setSelectedInvoice(null);
  };

  const getAvailableBatches = (productId) => {
    return inventory.filter(inv => inv.product_id === productId && inv.quantity > 0);
  };

  const handleReplacementItemChange = (index, field, value) => {
    const newItems = [...replacementItems];
    newItems[index][field] = value;
    
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].selling_price = product.selling_price;
        newItems[index].gst_percentage = product.gst_percentage || 12;
      }
    }
    
    setReplacementItems(newItems);
  };

  const addReplacementItem = () => {
    setReplacementItems([
      ...replacementItems,
      { product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }
    ]);
  };

  const removeReplacementItem = (index) => {
    if (replacementItems.length > 1) {
      setReplacementItems(replacementItems.filter((_, i) => i !== index));
    }
  };

  const filteredCreditNotes = creditNotes.filter(cn =>
    cn.credit_note_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cn.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cn.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Credit Notes</h1>
          <p className="text-slate-500 text-sm mt-1">Manage returns, exchanges and credit notes</p>
        </div>
        <Button
          onClick={() => { resetForm(); setIsCreateDialogOpen(true); }}
          className="btn-primary"
          data-testid="create-credit-note-btn"
        >
          <Plus className="w-4 h-4" />
          New Credit Note
        </Button>
      </div>

      {/* Search */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by credit note, invoice or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="credit-note-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase">Total Credit Notes</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{creditNotes.length}</p>
          </CardContent>
        </Card>
        <Card className="border border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-xs text-red-600 uppercase">Total Credit Value</p>
            <p className="text-2xl font-bold text-red-700 tabular-nums mt-1">
              {formatCurrency(creditNotes.reduce((sum, cn) => sum + cn.grand_total, 0))}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-600 uppercase">With Replacements</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">
              {creditNotes.filter(cn => cn.replacement_invoice_id).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Credit Notes Table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs">Credit Note #</TableHead>
                <TableHead className="text-xs">Date</TableHead>
                <TableHead className="text-xs">Original Invoice</TableHead>
                <TableHead className="text-xs">Customer</TableHead>
                <TableHead className="text-xs">Reason</TableHead>
                <TableHead className="text-xs text-right">Amount</TableHead>
                <TableHead className="text-xs">Replacement</TableHead>
                <TableHead className="text-xs text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCreditNotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    <RotateCcw className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>No credit notes found</p>
                    <p className="text-sm mt-1">Create a credit note for returns or exchanges</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCreditNotes.map((cn) => (
                  <TableRow key={cn.id} className="hover:bg-slate-50">
                    <TableCell>
                      <span className="font-mono text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-1 rounded">
                        {cn.credit_note_number}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(cn.credit_note_date)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-blue-600 font-mono">{cn.invoice_number}</span>
                    </TableCell>
                    <TableCell className="text-sm">{cn.customer_name}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        cn.reason === 'Defective' ? 'bg-red-100 text-red-700' :
                        cn.reason === 'Exchange' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {cn.reason}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-right tabular-nums font-medium text-red-600">
                      -{formatCurrency(cn.grand_total)}
                    </TableCell>
                    <TableCell>
                      {cn.replacement_invoice_number ? (
                        <span className="text-xs text-emerald-600 font-medium font-mono">{cn.replacement_invoice_number}</span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedCreditNote(cn);
                            setReplacementItems([{ product_id: '', batch_number: '', quantity: '', selling_price: '', gst_percentage: 12 }]);
                            setIsReplacementDialogOpen(true);
                          }}
                          className="text-xs text-teal-600 hover:text-teal-700"
                        >
                          <ArrowRight className="w-3 h-3 mr-1" />
                          Create
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedCreditNote(cn); setIsViewDialogOpen(true); }}
                          data-testid={`view-cn-${cn.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!cn.replacement_invoice_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(cn.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-cn-${cn.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Credit Note Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-teal-600" />
              Create Credit Note
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Invoice Selection */}
            <div className="space-y-2">
              <Label>Select Invoice *</Label>
              <Select value={formData.invoice_id} onValueChange={handleInvoiceSelect}>
                <SelectTrigger data-testid="cn-invoice-select">
                  <SelectValue placeholder="Select an invoice" />
                </SelectTrigger>
                <SelectContent>
                  {sales.map((sale) => (
                    <SelectItem key={sale.id} value={sale.id}>
                      {sale.invoice_number} - {sale.customer_name} ({formatCurrency(sale.grand_total)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selected Invoice Info */}
            {selectedInvoice && (
              <Card className="bg-slate-50 border border-slate-200">
                <CardContent className="p-3">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500 text-xs">Invoice</p>
                      <p className="font-medium">{selectedInvoice.invoice_number}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Customer</p>
                      <p className="font-medium">{selectedInvoice.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Total</p>
                      <p className="font-medium">{formatCurrency(selectedInvoice.grand_total)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reason */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reason *</Label>
                <Select value={formData.reason} onValueChange={(v) => setFormData({ ...formData, reason: v })}>
                  <SelectTrigger data-testid="cn-reason-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CREDIT_NOTE_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes"
                  data-testid="cn-notes-input"
                />
              </div>
            </div>

            {/* Items */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Items to Return/Credit</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add Item
                </Button>
              </div>
              
              {formData.items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-slate-50 rounded-lg">
                  <div className="col-span-4 space-y-1">
                    <Label className="text-xs">Product</Label>
                    <Select
                      value={item.product_id}
                      onValueChange={(v) => handleItemChange(index, 'product_id', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedInvoice?.items.map((invItem) => (
                          <SelectItem key={invItem.product_id} value={invItem.product_id}>
                            {invItem.product_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs">Batch</Label>
                    <Input
                      value={item.batch_number}
                      onChange={(e) => handleItemChange(index, 'batch_number', e.target.value)}
                      readOnly
                      className="bg-slate-100"
                    />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs">Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      placeholder="Qty"
                    />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs">Price</Label>
                    <Input
                      type="number"
                      value={item.selling_price}
                      onChange={(e) => handleItemChange(index, 'selling_price', e.target.value)}
                      readOnly
                      className="bg-slate-100"
                    />
                  </div>
                  <div className="col-span-1 space-y-1">
                    <Label className="text-xs">GST%</Label>
                    <Input
                      type="number"
                      value={item.gst_percentage}
                      onChange={(e) => handleItemChange(index, 'gst_percentage', e.target.value)}
                      readOnly
                      className="bg-slate-100"
                    />
                  </div>
                  <div className="col-span-1">
                    {formData.items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                Create Credit Note
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Credit Note Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Credit Note Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedCreditNote && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Credit Note Number</p>
                  <p className="font-bold text-teal-600">{selectedCreditNote.credit_note_number}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Date</p>
                  <p className="font-medium">{formatDate(selectedCreditNote.credit_note_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Original Invoice</p>
                  <p className="font-medium text-blue-600">{selectedCreditNote.invoice_number}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Reason</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    selectedCreditNote.reason === 'Defective' ? 'bg-red-100 text-red-700' :
                    selectedCreditNote.reason === 'Exchange' ? 'bg-amber-100 text-amber-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {selectedCreditNote.reason}
                  </span>
                </div>
              </div>

              {/* Customer Info */}
              <div className="p-3 border border-slate-200 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Customer</p>
                <p className="font-medium">{selectedCreditNote.customer_name}</p>
                <p className="text-sm text-slate-600">{selectedCreditNote.customer_gstin}</p>
              </div>

              {/* Items */}
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Product</TableHead>
                    <TableHead className="text-xs">Batch</TableHead>
                    <TableHead className="text-xs text-right">Qty</TableHead>
                    <TableHead className="text-xs text-right">Price</TableHead>
                    <TableHead className="text-xs text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedCreditNote.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="text-sm">{item.product_name}</TableCell>
                      <TableCell className="text-sm">{item.batch_number}</TableCell>
                      <TableCell className="text-sm text-right">{item.quantity}</TableCell>
                      <TableCell className="text-sm text-right">{formatCurrency(item.selling_price)}</TableCell>
                      <TableCell className="text-sm text-right font-medium">{formatCurrency(item.total_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Totals */}
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="flex justify-between text-sm mb-1">
                  <span>Taxable Amount:</span>
                  <span>{formatCurrency(selectedCreditNote.total_taxable)}</span>
                </div>
                {!selectedCreditNote.is_interstate ? (
                  <>
                    <div className="flex justify-between text-sm mb-1">
                      <span>CGST:</span>
                      <span>{formatCurrency(selectedCreditNote.total_cgst)}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>SGST:</span>
                      <span>{formatCurrency(selectedCreditNote.total_sgst)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-sm mb-1">
                    <span>IGST:</span>
                    <span>{formatCurrency(selectedCreditNote.total_igst)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-2 border-t border-red-200">
                  <span>Credit Total:</span>
                  <span className="text-red-600">-{formatCurrency(selectedCreditNote.grand_total)}</span>
                </div>
              </div>

              {/* Replacement Info */}
              {selectedCreditNote.replacement_invoice_number && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-xs text-emerald-600 mb-1">Replacement Invoice Created</p>
                  <p className="font-medium text-emerald-700">{selectedCreditNote.replacement_invoice_number}</p>
                </div>
              )}

              {selectedCreditNote.notes && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-600 mb-1">Notes</p>
                  <p className="text-sm">{selectedCreditNote.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Replacement Invoice Dialog */}
      <Dialog open={isReplacementDialogOpen} onOpenChange={setIsReplacementDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              Create Replacement Invoice
            </DialogTitle>
          </DialogHeader>
          
          {selectedCreditNote && (
            <form onSubmit={handleCreateReplacement} className="space-y-4">
              <Card className="bg-slate-50 border border-slate-200">
                <CardContent className="p-3">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500 text-xs">Credit Note</p>
                      <p className="font-medium text-teal-600">{selectedCreditNote.credit_note_number}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Customer</p>
                      <p className="font-medium">{selectedCreditNote.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Credit Amount</p>
                      <p className="font-medium text-red-600">-{formatCurrency(selectedCreditNote.grand_total)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Items */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Replacement Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addReplacementItem}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Item
                  </Button>
                </div>
                
                {replacementItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-emerald-50 rounded-lg">
                    <div className="col-span-3 space-y-1">
                      <Label className="text-xs">Product</Label>
                      <Select
                        value={item.product_id}
                        onValueChange={(v) => handleReplacementItemChange(index, 'product_id', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3 space-y-1">
                      <Label className="text-xs">Batch</Label>
                      <Select
                        value={item.batch_number}
                        onValueChange={(v) => handleReplacementItemChange(index, 'batch_number', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableBatches(item.product_id).map((inv) => (
                            <SelectItem key={inv.batch_number} value={inv.batch_number}>
                              {inv.batch_number} (Qty: {inv.quantity})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleReplacementItemChange(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Price</Label>
                      <Input
                        type="number"
                        value={item.selling_price}
                        onChange={(e) => handleReplacementItemChange(index, 'selling_price', e.target.value)}
                      />
                    </div>
                    <div className="col-span-1 space-y-1">
                      <Label className="text-xs">GST%</Label>
                      <Input
                        type="number"
                        value={item.gst_percentage}
                        onChange={(e) => handleReplacementItemChange(index, 'gst_percentage', e.target.value)}
                      />
                    </div>
                    <div className="col-span-1">
                      {replacementItems.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeReplacementItem(index)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsReplacementDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  Create Replacement Invoice
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditNotes;
